// src/types/CommandTypes.ts

// ---------------------------------------------------------------------------
// Canonical command name constants — single source of truth for all repos.
// Import these everywhere; never write command name string literals inline.
// ---------------------------------------------------------------------------

export const CMD_REAR_FW_DWNLD     = 'REAR_FW_DWNLD'     as const;
export const CMD_REAR_LOG_UPLD     = 'REAR_LOG_UPLD'     as const;
export const CMD_REAR_DELETE_LOGS  = 'REAR_DELETE_LOGS'  as const;
export const CMD_REAR_TOGGLE_LED   = 'REAR_TOGGLE_LED'   as const;
export const CMD_REAR_APP_MODE     = 'REAR_APP_MODE'     as const;
export const CMD_REAR_RACE_MODE    = 'REAR_RACE_MODE'    as const;
export const CMD_REAR_SLEEP        = 'REAR_SLEEP'        as const;
export const CMD_REAR_REBOOT       = 'REAR_REBOOT'       as const;
export const CMD_REAR_WINTER       = 'REAR_WINTER'       as const;
export const CMD_REAR_MODEM_DFOTA  = 'REAR_MODEM_DFOTA'  as const;

export const CMD_FRONT_FW_DWNLD    = 'FRONT_FW_DWNLD'    as const;
export const CMD_FRONT_LOG_UPLD    = 'FRONT_LOG_UPLD'    as const;
export const CMD_FRONT_DELETE_LOGS = 'FRONT_DELETE_LOGS' as const;
export const CMD_FRONT_TOGGLE_LED  = 'FRONT_TOGGLE_LED'  as const;

// Core command operation type — derived from the constants above.
export type CommandOperation =
    | typeof CMD_REAR_FW_DWNLD
    | typeof CMD_REAR_LOG_UPLD
    | typeof CMD_REAR_APP_MODE
    | typeof CMD_REAR_RACE_MODE
    | typeof CMD_REAR_SLEEP
    | typeof CMD_REAR_REBOOT
    | typeof CMD_REAR_TOGGLE_LED
    | typeof CMD_REAR_DELETE_LOGS
    | typeof CMD_REAR_WINTER
    | typeof CMD_REAR_MODEM_DFOTA
    | 'SPRING';

export type logKind = 'race' | 'debug';

// Shape of the command request you send to the backend
export interface CommandRequest {
    operation: CommandOperation;
    deviceIds: string[];
    /** Front or rear command scope selected in UI. */
    commandTarget?: 'rear' | 'front';
    eventId?: string;
    raceId?: string;
    // Only used for FW_DWNLD
    targetVersion?: string;
    /** Rear (default) or front firmware artifact; front updates proxy through the rear unit. */
    firmwareTarget?: 'rear' | 'front';
    // Optional command id passthrough for command endpoints
    commandId?: string;
    // Only used for LOG_UPLD
    logKind?: logKind;
    // Optional grouping folder for LOG_UPLD
    logFolder?: string;
    // Only used for MODEM_DFOTA — CloudFront HTTPS URL to the .par delta package
    modemFwUrl?: string;
}

// Generic API result shape for command submissions
export interface CommandApiResult {
    ok: boolean;
    status?: number;
    data?: unknown;
    error?: string;
}

// Logical DynamoDB item schema for commands table
export interface CommandDdbItem {
    commandId: string;
    deviceId: string;
    requestId: string;

    type?: string;
    targetVersion?: string;

    requestStatusCode?: number;
    status?: string;

    status_info?: string;
    updatedAt?: number;

    [key: string]: unknown;
}

// Parsed status_info
export interface CommandAck {
    receivedAt?: number;
    accepted?: boolean;
    state?: string;
    reason?: string;
    bytes?: number;
    total?: number;
    percent?: number;
}

export type CommandRow = {
    ddb: CommandDdbItem;
    ack: CommandAck | null;
};

export type CommandRowMap = Record<string, CommandRow>;

export type SortKey =
    | 'commandId'
    | 'deviceId'
    | 'type'
    | 'targetVersion'
    | 'requestStatusCode'
    | 'status'
    | 'ackState'
    | 'ackReason'
    | 'ackAccepted'
    | 'ackPercent'
    | 'updatedAt';

export type SortDirection = 'asc' | 'desc';

export type SortConfig = {
    key: SortKey;
    direction: SortDirection;
} | null;
